import { StandardEvents } from '@shopify/events';
const { canSpeculate, rememberImage, prepareImage, displayImage } = await import(
  document.querySelector('script[data-tcs-media-cache]')?.dataset.tcsMediaCache ||
    './tcs-variant-image-cache.js'
);

// Horizon 4.1.3 coupling: media-gallery.js consumes the first media-gallery from
// ProductSelectEvent.promise in a BUBBLE listener. Claim response galleries in
// a CAPTURE-registered promise reaction, before that listener can expose them.
// Horizon already consumes nodes from this same document for price/form/SKU.
// Only gallery nodes are detached; the event, promise and commerce nodes remain
// unchanged. No core patch, global method override or second gallery swap.
const responseGalleries = new WeakMap();
const imageTimeoutMs = 10000;
const responseTimeoutMs = 30000;
const feedbackDelayMs = 200;
const focusable = 'button, a[href], input, select, [tabindex]';
const activationKeys = new Set([' ', 'Enter']);
const staleMedia = [
  ':scope > slideshow-component slideshow-slide:not(:first-child)',
  ':scope > slideshow-component slideshow-controls',
  ':scope > slideshow-component slideshow-arrows',
  ':scope > .media-gallery__grid > li:not(:first-child)',
  '.tcs-pdp-media-zoom',
].join(', ');
const staleOpeners = ['.tcs-viewer-fallbacks'].join(', ');
const staleActivation =
  'slideshow-controls, slideshow-arrows, .tcs-pdp-media-zoom, .tcs-viewer-fallbacks, [data-tcs-viewer-open], slideshow-slide:not(:first-child), .media-gallery__grid > li:not(:first-child)';

function claimGalleries(html) {
  if (!responseGalleries.has(html)) {
    const galleries = [...html.querySelectorAll('media-gallery')];
    // Claim all galleries, including other product sections in a full-page
    // response, so Horizon cannot accidentally consume the next unrelated one.
    for (const gallery of galleries) gallery.remove();
    responseGalleries.set(html, galleries);
  }
  return responseGalleries.get(html);
}

function setBusy(gallery, busy) {
  // Keep tcs-pdp-variant-media's live status outside every busy subtree so it
  // can announce progress and failures while visual controls are pending.
  for (const region of gallery.querySelectorAll(
    ':scope > slideshow-component, :scope > .media-gallery__grid, :scope > slideshow-component slideshow-controls',
  )) {
    if (busy) region.setAttribute('aria-busy', 'true');
    else region.removeAttribute('aria-busy');
  }
}

function primaryImage(gallery) {
  const grid = gallery.dataset.presentation === 'grid' && matchMedia('(min-width: 750px)').matches;
  return gallery.querySelector(
    grid
      ? '.media-gallery__grid > li:first-child .product-media__image'
      : 'slideshow-slide:first-child .product-media__image',
  );
}

function slotSizes(gallery, fallback) {
  const width = primaryImage(gallery)?.getBoundingClientRect().width;
  return width > 0 ? `${Math.ceil(width)}px` : fallback;
}

function isVisible(element) {
  if (!(element instanceof Element) || !element.getClientRects().length) return false;
  const style = getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function activeGalleryFocus(gallery) {
  const active = document.activeElement;
  if (!(active instanceof Element) || !gallery.contains(active)) return null;
  const thumb = active.closest(
    '.slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail',
  );
  if (thumb) {
    const list = thumb.closest('.slideshow-controls__thumbnails, .dialog-thumbnails-list');
    const index = list ? [...list.querySelectorAll('button')].indexOf(thumb) : -1;
    return index >= 0
      ? {
          type: list.classList.contains('dialog-thumbnails-list')
            ? 'dialog-thumbnail'
            : 'thumbnail',
          index,
        }
      : null;
  }
  if (active.matches('.thumb')) return { type: 'fixture-thumbnail' };
  if (
    active.closest(
      '[data-tcs-viewer-open], .tcs-pdp-media-zoom, .product-media-container__zoom-button',
    )
  )
    return { type: 'opener' };
  return null;
}

function restoreGalleryFocus(next, focus) {
  if (!focus) return;
  let target;
  if (focus.type === 'thumbnail') {
    target = next.querySelectorAll('.slideshow-controls__thumbnails button')[focus.index];
  } else if (focus.type === 'dialog-thumbnail') {
    target = next.querySelectorAll('.dialog-thumbnails-list button')[focus.index];
  } else if (focus.type === 'opener') {
    target = next.querySelector(
      '[data-tcs-viewer-open], .tcs-pdp-media-zoom button, .product-media-container__zoom-button',
    );
  } else if (focus.type === 'fixture-thumbnail') {
    target = next.querySelector('.thumb');
  }
  target?.focus?.({ preventScroll: true });
}

function shouldRestoreGalleryFocus(gallery, focus) {
  if (!focus) return false;
  const active = document.activeElement;
  return (
    !(active instanceof Element) ||
    active === document.body ||
    active === gallery ||
    gallery.contains(active)
  );
}

function visibleThumbnailImages(next, gallery) {
  const currentLists = [
    ...gallery.querySelectorAll('.slideshow-controls__thumbnails, .dialog-thumbnails-list'),
  ];
  const nextLists = [
    ...next.querySelectorAll('.slideshow-controls__thumbnails, .dialog-thumbnails-list'),
  ];
  const images = [];
  currentLists.forEach((list, index) => {
    if (!isVisible(list) || list.closest('dialog:not([open])')) return;
    images.push(...(nextLists[index]?.querySelectorAll('img') || []));
  });
  if (!images.length && next.dataset.presentation !== 'grid') {
    images.push(
      ...(next
        .querySelector(':scope > slideshow-component .slideshow-controls__thumbnails')
        ?.querySelectorAll('img') || []),
    );
  }
  return images;
}

function waitForImage(image, signal) {
  if (image.complete && image.naturalWidth) return Promise.resolve();
  const decoded =
    typeof image.decode === 'function'
      ? image.decode()
      : new Promise((resolve, reject) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', reject, { once: true });
        });
  return abortable(decoded, signal);
}

function setStatus(status, text = '', { visual = false } = {}) {
  if (!status) return;
  status.textContent = text;
  status.toggleAttribute('data-tcs-visual-status', Boolean(text && visual));
}

async function prepareVisibleThumbnails(next, gallery, signal) {
  const initialTree = primaryImage(next)
    ? next.dataset.presentation === 'grid' && matchMedia('(min-width: 750px)').matches
      ? 'grid'
      : 'slide'
    : '';
  const images = visibleThumbnailImages(next, gallery);
  for (const image of images) image.loading = 'eager';
  await Promise.all(
    images.map((image) =>
      waitForImage(image, signal).catch((error) => {
        if (signal.aborted || error?.name === 'AbortError' || error?.name === 'TimeoutError')
          throw error;
      }),
    ),
  );
  const currentTree =
    next.dataset.presentation === 'grid' && matchMedia('(min-width: 750px)').matches
      ? 'grid'
      : 'slide';
  if (initialTree && currentTree !== initialTree) {
    const retry = visibleThumbnailImages(next, gallery);
    for (const image of retry) image.loading = 'eager';
    await Promise.all(
      retry.map((image) =>
        waitForImage(image, signal).catch((error) => {
          if (signal.aborted || error?.name === 'AbortError' || error?.name === 'TimeoutError')
            throw error;
        }),
      ),
    );
  }
}

// decode() itself is not abortable. Race it against cancellation, and consume
// late resolution/rejection without letting it reach the visible gallery.
function abortable(promise, signal) {
  return new Promise((resolve, reject) => {
    const aborted = () => reject(signal.reason);
    if (signal.aborted) return aborted();
    signal.addEventListener('abort', aborted, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', aborted));
  });
}

class TcsPdpVariantMedia extends HTMLElement {
  #lifetime;
  #request;
  #resize;
  #visible;
  #warm;
  #map;
  #scope;

  #guardStale = (event) => {
    if (
      event.type === 'keydown' &&
      (!activationKeys.has(event.key) || event.altKey || event.ctrlKey || event.metaKey)
    )
      return;
    const gallery = this.closest('media-gallery');
    if (!gallery?.hasAttribute('data-tcs-stale-media')) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest(staleActivation)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #candidate(target) {
    const picker = target.closest('variant-picker');
    if (!picker || !this.#map) return null;
    const control =
      target.closest('label')?.querySelector('input') ||
      (target instanceof HTMLSelectElement ? target.selectedOptions[0] : target);
    const candidate = this.#map.candidates.find(
      (item) => item.option === control?.dataset.optionValueId,
    );
    if (!candidate) return null;
    // A manifest describes changing ONE option relative to its rendered context.
    // During rapid multi-option changes do not trust a stale data-variant-id.
    const selected = [...picker.querySelectorAll('fieldset input:checked, select')].map((input) =>
      input instanceof HTMLSelectElement ? input.selectedOptions[0]?.value : input.value,
    );
    if (
      selected.length !== this.#map.selected.length ||
      selected.some(
        (value, index) => index !== candidate.position && value !== this.#map.selected[index],
      )
    )
      return null;
    return candidate;
  }

  // Same-shade retry. A failed selection leaves its radio checked, and a checked
  // radio never fires `change`, the only event Horizon's VariantPicker requests
  // on. Re-activating it still reaches us: pointer/label activation fires
  // `click`; Space on a checked radio fires only keyup in Chromium (no click).
  // Applies only while this coordinator's current request has failed, and only
  // to a checked radio inside this coordinator's scope whose option value id is
  // the failed request's; every other activation is left to Horizon.
  //
  // Horizon 4.1.3 coupling: re-request through VariantPicker's public
  // buildRequestUrl() + fetchUpdatedSection(), with the same arguments its
  // variantChanged() passes for a non-connected option. Do NOT replay `change`:
  // variantChanged() -> updateSelectedOption() would push the same radio index
  // twice into its private checked history and leave no radio
  // data-current-checked="true" (morph.js preserves those attributes). These
  // two methods leave checked state and the URL exactly as the original
  // selection set them. A connected (combined-listing) product option, which
  // Horizon loads with a whole-<main> morph, is not retried here. The request
  // synchronously dispatches ProductSelectEvent, so #select replaces the failed
  // request at once and a click that follows the same Space press in other
  // engines finds nothing to retry.
  #retry = (event) => {
    if (
      event.type === 'keyup' &&
      (event.key !== ' ' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
    )
      return;
    const input = event.target;
    const request = this.#request;
    const picker = input instanceof HTMLInputElement ? input.closest('variant-picker') : null;
    if (
      !request?.failed ||
      !picker ||
      input.type !== 'radio' ||
      !input.checked ||
      !input.dataset.optionValueId ||
      input.dataset.optionValueId !== request.option ||
      !this.#scope.contains(input) ||
      typeof picker.buildRequestUrl !== 'function' ||
      typeof picker.fetchUpdatedSection !== 'function'
    )
      return;
    const { optionValueId, variantId = '', connectedProductUrl = '' } = input.dataset;
    if (connectedProductUrl && connectedProductUrl !== picker.dataset.productUrl?.split('?')[0])
      return;
    picker.fetchUpdatedSection(picker.buildRequestUrl(input), {
      morphElementSelector: picker.closest('featured-product-information')
        ? 'featured-product-information'
        : undefined,
      detail: { optionValueId, variantId, connectedProductUrl },
    });
  };

  #intent = (event) => {
    if (!canSpeculate()) return;
    const candidate = this.#candidate(event.target);
    const gallery = this.closest('media-gallery');
    if (candidate && this.#scope.contains(event.target))
      prepareImage(candidate.image, primaryImage(gallery)?.getBoundingClientRect().width || 600, {
        signal: this.#lifetime.signal,
        priority: 'intent',
      }).catch(() => {});
  };

  #prepare() {
    const gallery = this.closest('media-gallery');
    const hero = primaryImage(gallery);
    if (
      !this.#map ||
      !canSpeculate() ||
      !hero?.complete ||
      !hero.naturalWidth ||
      !hero.getBoundingClientRect().width ||
      this.closest('dialog:not([open])')
    )
      return;
    rememberImage(hero);
    if (this.#warm && !this.#warm.signal.aborted) return;
    this.#warm = new AbortController();
    const seen = new Set([hero.closest('[data-media-id]')?.dataset.mediaId]);
    let count = 0;
    for (const { image } of this.#map.candidates) {
      if (seen.has(image.id)) continue;
      seen.add(image.id);
      prepareImage(image, hero.getBoundingClientRect().width, { signal: this.#warm.signal }).catch(
        () => {},
      );
      if (++count === 4) break;
    }
  }

  async #fast(candidate, gallery, request) {
    if (!candidate || gallery.querySelector('[ref="zoomDialogComponent"] dialog[open]')) return;
    let old = primaryImage(gallery);
    if (!old || old.parentElement.querySelector('deferred-media, product-model')) return;
    const media = candidate.image;
    let image;
    do {
      old = primaryImage(gallery);
      if (!old) return;
      image = await displayImage(media, old.getBoundingClientRect().width, {
        signal: request.signal,
      });
      request.signal.throwIfAborted();
    } while (
      old !== primaryImage(gallery) ||
      image.sizes !== `${Math.ceil(Math.max(1, old.getBoundingClientRect().width))}px`
    );
    request.signal.throwIfAborted();
    // A section response that already failed keeps the previous complete gallery.
    if (
      request.failed ||
      !this.isConnected ||
      gallery.querySelector('[ref="zoomDialogComponent"] dialog[open]')
    )
      return;
    const targets = [
      gallery.querySelector('slideshow-slide:first-child .product-media__image'),
      gallery.querySelector('.media-gallery__grid > li:first-child .product-media__image'),
      gallery.querySelector(
        'zoom-dialog .dialog-zoomed-gallery > li:first-child .product-media__image',
      ),
    ].filter(Boolean);
    // Preserve native wrapper controls/refs and synchronize the zoom slot before
    // exposing the hero. The complete server gallery reconciles the other slots.
    for (const target of targets) {
      const replacement = target === old ? image : image.cloneNode();
      replacement.className = target.className;
      for (const attr of ['ref', 'data-testid']) {
        if (target.hasAttribute(attr)) replacement.setAttribute(attr, target.getAttribute(attr));
      }
      replacement.setAttribute('data_max_resolution', media.src);
      replacement.style.setProperty('--focal-point', media.focal);
      const wrapper = target.closest('.product-media');
      if (wrapper) {
        wrapper.dataset.mediaId = media.id;
        wrapper.style.setProperty('--ratio', media.width / media.height);
      }
      const container = target.closest('slideshow-slide, li');
      if (container?.style.getPropertyValue('--media-preview-ratio'))
        container.style.setProperty('--media-preview-ratio', media.width / media.height);
      target.replaceWith(replacement);
    }
    for (const thumbnails of gallery.querySelectorAll(
      '.slideshow-controls__thumbnails, .dialog-thumbnails-list',
    )) {
      const button = thumbnails.querySelector('button');
      if (!button) continue;
      const thumbnail = image.cloneNode();
      thumbnail.removeAttribute('fetchpriority');
      if (thumbnails.classList.contains('dialog-thumbnails-list')) {
        // The dialog list button is named by aria-label, which replaceChildren
        // does not touch. R-036a leaves this surface alone.
        button.setAttribute('aria-label', media.alt || button.getAttribute('aria-label'));
        button.replaceChildren(thumbnail);
        continue;
      }
      // R-036c: the GALLERY rail button is named by its CONTENT (R-036a route 1),
      // so the visually-hidden span carrying the position has to survive this
      // swap. Replacing every child leaves the button named by the new alt alone
      // — and named by nothing at all when the medium has no alt, which is the
      // unnamed control R-036a's safety property forbids. Order matches the
      // server render: image, then span, with no whitespace between them.
      // Badges are still dropped; they describe the medium being replaced.
      const position = button.querySelector(':scope > .visually-hidden');
      if (position) {
        button.replaceChildren(thumbnail, position);
        continue;
      }
      // No position span to preserve — markup predating R-036a, or mutated
      // since. Recreate a content-name fallback instead of restoring aria-label,
      // so an alt-less medium is still not exposed as an unnamed button.
      const siblingButtons = thumbnails.querySelectorAll('button');
      const fallbackPosition = document.createElement('span');
      fallbackPosition.className = 'visually-hidden';
      fallbackPosition.textContent = `Slide ${
        Array.prototype.indexOf.call(siblingButtons, button) + 1
      } of ${siblingButtons.length}`;
      const previous = button.querySelector('img');
      if (previous) previous.replaceWith(thumbnail);
      else button.prepend(thumbnail);
      thumbnail.after(fallbackPosition);
    }
    gallery
      .querySelector(':scope > slideshow-component')
      ?.select?.(0, undefined, { animate: false });
    // R-031 follow-up: in an explicit variant gallery every non-hero item still
    // belongs to the previous variant. Keep it visible as a stable pending shell,
    // but make it inert and guard activation until #update atomically replaces
    // the whole gallery with the server-rendered one.
    if (this.hasAttribute('data-variant-gallery')) {
      for (const element of gallery.querySelectorAll(staleMedia)) {
        element.inert = true;
        element.classList.add('tcs-variant-media-stale');
      }
      for (const element of gallery.querySelectorAll(staleOpeners)) element.inert = true;
      gallery.toggleAttribute('data-tcs-stale-media', true);
    }
    // R-027: native fallbacks remain truthful even if the PDP viewer module failed.
    for (const link of gallery.querySelectorAll('a[data-tcs-viewer-open="0"]')) {
      link.href = media.src;
    }
    request.fast = true;
    const progress = this.querySelector('progress');
    if (progress) progress.hidden = true;
    gallery.dataset.tcsSelectedMedia = media.id;
    if (!request.failed) setStatus(this.querySelector('[role="status"]'));
    this.dispatchEvent(
      new CustomEvent('tcs:variant-image-painted', {
        bubbles: true,
        detail: {
          mediaId: media.id,
          variantId: candidate.variant,
          prepared: image.dataset.tcsPreparedAtSelection === 'true',
        },
      }),
    );
  }

  connectedCallback() {
    const gallery = this.closest('media-gallery');
    const section = gallery?.closest('.shopify-section, dialog');
    if (!section || this.closest('product-card')) return;
    this.#scope = gallery.closest(
      'product-component, featured-product-information, dialog, .shopify-section',
    );
    try {
      this.#map = JSON.parse(this.querySelector('[data-tcs-media-map]')?.textContent || 'null');
    } catch {
      this.#map = null;
    }
    this.#lifetime = new AbortController();
    // Preserve responsive quality after a staged image adopts a measured slot.
    this.#resize = new ResizeObserver(() => {
      const image = primaryImage(gallery);
      if (image) image.sizes = slotSizes(gallery, image.sizes);
    });
    this.#resize.observe(gallery);
    this.#visible = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#prepare();
        else this.#warm?.abort();
      }
    });
    this.#visible.observe(gallery);
    primaryImage(gallery)?.addEventListener(
      'load',
      () => {
        const rect = gallery.getBoundingClientRect();
        if (rect.top < innerHeight && rect.bottom > 0) this.#prepare();
      },
      { signal: this.#lifetime.signal, once: true },
    );
    for (const type of ['pointerover', 'focusin', 'pointerdown'])
      this.#scope.addEventListener(type, this.#intent, { signal: this.#lifetime.signal });
    for (const type of ['click', 'keyup'])
      this.#scope.addEventListener(type, this.#retry, { signal: this.#lifetime.signal });
    for (const type of ['click', 'keydown'])
      gallery.addEventListener(type, this.#guardStale, {
        capture: true,
        signal: this.#lifetime.signal,
      });
    this.closest('dialog')?.addEventListener(
      'close',
      () => {
        this.#warm?.abort();
        this.#request?.abort();
      },
      { signal: this.#lifetime.signal },
    );
    section.addEventListener(StandardEvents.productSelect, this.#select, {
      capture: true,
      signal: this.#lifetime.signal,
    });
  }

  disconnectedCallback() {
    this.#resize?.disconnect();
    this.#visible?.disconnect();
    this.#warm?.abort();
    this.#lifetime?.abort();
    this.#request?.abort();
  }

  #select = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (
      event.target.closest(
        'product-component, featured-product-information, dialog, .shopify-section',
      ) !== this.#scope
    )
      return;

    this.#request?.abort();
    const request = new AbortController();
    request.option = event.detail?.optionValueId || '';
    this.#request = request;
    const gallery = this.closest('media-gallery');
    const status = this.querySelector('[role="status"]');
    setStatus(status);
    setBusy(gallery, true);
    const picker = event.target.closest('variant-picker');
    const control = picker?.querySelector(
      `[data-option-value-id="${CSS.escape(event.detail?.optionValueId || '')}"]`,
    );
    const candidate = control ? this.#candidate(control) : null;
    this.#fast(candidate, gallery, request).catch(() => {});
    const progress = this.querySelector('progress');
    if (progress) progress.hidden = true;
    const feedback = setTimeout(() => {
      if (!request.fast) {
        setStatus(status, this.dataset.loading || '', { visual: true });
        if (progress) progress.hidden = false;
      }
    }, feedbackDelayMs);
    const expire = () => request.abort(new DOMException('Variant media timed out', 'TimeoutError'));
    // Section HTML can be large on a slow connection. Give it its own bounded
    // budget; it must not consume the image's load/decode deadline.
    let timeout = setTimeout(expire, responseTimeoutMs);

    // This reaction MUST be registered synchronously during capture, not after
    // an await, to precede Horizon's bubble listener even for a cached response.
    const response = event.promise.then(({ detail }) => {
      if (!detail?.html) return null;
      const galleries = claimGalleries(detail.html);
      if (!request.signal.aborted) {
        clearTimeout(timeout);
        timeout = setTimeout(expire, imageTimeoutMs);
      }
      return galleries.find(
        (candidate) =>
          candidate.querySelector('tcs-pdp-variant-media')?.dataset.key === this.dataset.key,
      );
    });

    this.#update(response, gallery, request.signal, () => clearTimeout(timeout))
      .catch((error) => {
        if (this.#request === request && this.isConnected && error?.name !== 'AbortError') {
          request.failed = true;
          // An image without alternative text must not announce a bare ": ".
          const alt = primaryImage(gallery)?.alt?.trim();
          setStatus(status, alt ? `${alt}: ${this.dataset.error}` : this.dataset.error || '', {
            visual: !request.fast,
          });
        }
      })
      .finally(() => {
        clearTimeout(feedback);
        if (progress && this.#request === request) progress.hidden = true;
        clearTimeout(timeout);
        if (this.#request === request) {
          setBusy(gallery, false);
          if (status.textContent === this.dataset.loading) setStatus(status);
        }
      });
  };

  async #update(response, gallery, signal, ready = () => {}) {
    const next = await abortable(response, signal);
    if (!this.isConnected) return;
    signal.throwIfAborted();
    // Horizon resolves with any parsed body (it never checks response.ok), so a
    // throttled, error or password page arrives as html without this keyed
    // gallery. That is a failed response, not a no-op: reject into #select's
    // existing failure path so request.failed stops a pending fast paint and the
    // status region announces it. A hero already painted stays the new shade and
    // the previous shade's hidden media are never restored beside it.
    if (!next) throw new Error('Variant gallery missing from response');
    // Adopt the staged tree without starting its offscreen full-size images.
    // The decoded hero must stay in the active document when placed in this tree.
    for (const image of next.querySelectorAll(
      '.product-media__image, .deferred-media__poster-image',
    )) {
      image.loading = 'lazy';
    }
    document.adoptNode(next);

    // The server-rendered document is inert. Build only the primary image in
    // the active document; preserve its responsive sources and all Horizon refs.
    // Inserting this SAME decoded element avoids warming one image and showing
    // a second undecoded element. Never use data_max_resolution (zoom's 3840px).
    let target;
    do {
      target = primaryImage(next);
      if (!(target instanceof HTMLImageElement)) throw new Error('Variant preview missing');
      const descriptor = {
        src: target.getAttribute('src'),
        alt: target.alt,
        width: target.width,
        height: target.height,
      };
      const image = await displayImage(
        descriptor,
        primaryImage(gallery)?.getBoundingClientRect().width || 600,
        { signal },
      );
      for (const { name, value } of target.attributes) {
        if (!['src', 'srcset', 'sizes', 'loading', 'width', 'height', 'alt'].includes(name))
          image.setAttribute(name, value);
      }
      image.loading = 'eager';
      // Keep the size that was actually decoded. If the viewport changed while
      // awaiting decode, the loop below must prepare that new size before swap.
      signal.throwIfAborted();
      target.replaceWith(image);
      target = image;
      // A resize across the grid/mobile breakpoint while loading must prepare
      // the newly visible tree too. The image-phase timeout bounds retries.
    } while (primaryImage(next) !== target || target.sizes !== slotSizes(gallery, target.sizes));

    await prepareVisibleThumbnails(next, gallery, signal);
    ready();
    // Leave a zoom interaction that started during loading intact. Closing the
    // native dialog releases its scroll/focus lifecycle before replacing it.
    const dialog = gallery.querySelector(
      '[ref="zoomDialogComponent"] dialog[open], :scope > dialog[open]',
    );
    if (dialog) {
      await abortable(
        new Promise((resolve) => dialog.addEventListener('close', resolve, { once: true, signal })),
        signal,
      );
      // Viewport may have changed while zoom was open; retry against that layout.
      if (primaryImage(next) !== target || target.sizes !== slotSizes(gallery, target.sizes))
        return this.#update(Promise.resolve(next), gallery, signal, ready);
    }
    signal.throwIfAborted();
    if (!this.isConnected) return;

    const focus = activeGalleryFocus(gallery);
    const restoreFocus = shouldRestoreGalleryFocus(gallery, focus);
    gallery.replaceWith(next);
    if (restoreFocus) restoreGalleryFocus(next, focus);
  }
}

if (!customElements.get('tcs-pdp-variant-media')) {
  customElements.define('tcs-pdp-variant-media', TcsPdpVariantMedia);
}
